import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import './commentsEditorContribution.js';
import './commentService.js';
import { ICommentService } from 'vscode/vscode/vs/workbench/contrib/comments/browser/commentService.service';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { Disposable, MutableDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/workbench/common/contributions';
import { NumberBadge } from 'vscode/vscode/vs/workbench/services/activity/common/activity';
import { IActivityService } from 'vscode/vscode/vs/workbench/services/activity/common/activity.service';
import { COMMENTS_VIEW_ID } from 'vscode/vscode/vs/workbench/contrib/comments/browser/commentsTreeViewer';
import { CommentThreadState } from 'vscode/vscode/vs/editor/common/languages';
import { registerAction2, MenuId, Action2 } from 'vscode/vscode/vs/platform/actions/common/actions';
import { CONTEXT_KEY_HAS_COMMENTS, CONTEXT_KEY_SOME_COMMENTS_EXPANDED } from 'vscode/vscode/vs/workbench/contrib/comments/browser/commentsView';
import { ViewAction } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPane';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { revealCommentThread } from 'vscode/vscode/vs/workbench/contrib/comments/browser/commentsController';
import { accessibleViewIsShown, accessibleViewCurrentProviderId } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibilityConfiguration';

const _moduleId = "vs/workbench/contrib/comments/browser/comments.contribution";
registerAction2(class Collapse extends ViewAction {
    constructor() {
        super({
            viewId: COMMENTS_VIEW_ID,
            id: 'comments.collapse',
            title: ( localizeWithPath(_moduleId, 0, "Collapse All")),
            f1: false,
            icon: Codicon.collapseAll,
            menu: {
                id: MenuId.ViewTitle,
                group: 'navigation',
                when: ( (ContextKeyExpr.and( (ContextKeyExpr.and(
                     (ContextKeyExpr.equals('view', COMMENTS_VIEW_ID)),
                    CONTEXT_KEY_HAS_COMMENTS
                )), CONTEXT_KEY_SOME_COMMENTS_EXPANDED))),
                order: 100
            }
        });
    }
    runInView(_accessor, view) {
        view.collapseAll();
    }
});
registerAction2(class Expand extends ViewAction {
    constructor() {
        super({
            viewId: COMMENTS_VIEW_ID,
            id: 'comments.expand',
            title: ( localizeWithPath(_moduleId, 1, "Expand All")),
            f1: false,
            icon: Codicon.expandAll,
            menu: {
                id: MenuId.ViewTitle,
                group: 'navigation',
                when: ( (ContextKeyExpr.and( (ContextKeyExpr.and(
                     (ContextKeyExpr.equals('view', COMMENTS_VIEW_ID)),
                    CONTEXT_KEY_HAS_COMMENTS
                )), ContextKeyExpr.not(CONTEXT_KEY_SOME_COMMENTS_EXPANDED.key)))),
                order: 100
            }
        });
    }
    runInView(_accessor, view) {
        view.expandAll();
    }
});
registerAction2(class Reply extends Action2 {
    constructor() {
        super({
            id: 'comments.reply',
            title: ( localizeWithPath(_moduleId, 2, "Reply")),
            icon: Codicon.reply,
            precondition: ( (ContextKeyExpr.equals('canReply', true))),
            menu: [{
                    id: MenuId.CommentsViewThreadActions,
                    order: 100
                },
                {
                    id: MenuId.AccessibleView,
                    when: ( (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
                        accessibleViewCurrentProviderId.key,
                        "comments"
                    ))))),
                }]
        });
    }
    run(accessor, marshalledCommentThread) {
        const commentService = accessor.get(ICommentService);
        const editorService = accessor.get(IEditorService);
        const uriIdentityService = accessor.get(IUriIdentityService);
        revealCommentThread(commentService, editorService, uriIdentityService, marshalledCommentThread.thread, marshalledCommentThread.thread.comments[marshalledCommentThread.thread.comments.length - 1], true);
    }
});
( (Registry.as(Extensions.Configuration))).registerConfiguration({
    id: 'comments',
    order: 20,
    title: ( localizeWithPath(_moduleId, 3, "Comments")),
    type: 'object',
    properties: {
        'comments.openPanel': {
            enum: ['neverOpen', 'openOnSessionStart', 'openOnSessionStartWithComments'],
            default: 'openOnSessionStartWithComments',
            description: ( localizeWithPath(_moduleId, 4, "Controls when the comments panel should open.")),
            restricted: false,
            markdownDeprecationMessage: ( localizeWithPath(
                _moduleId,
                5,
                "This setting is deprecated in favor of `comments.openView`."
            ))
        },
        'comments.openView': {
            enum: ['never', 'file', 'firstFile', 'firstFileUnresolved'],
            enumDescriptions: [( localizeWithPath(_moduleId, 6, "The comments view will never be opened.")), ( localizeWithPath(
                _moduleId,
                7,
                "The comments view will open when a file with comments is active."
            )), ( localizeWithPath(
                _moduleId,
                8,
                "If the comments view has not been opened yet during this session it will open the first time during a session that a file with comments is active."
            )), ( localizeWithPath(
                _moduleId,
                9,
                "If the comments view has not been opened yet during this session and the comment is not resolved, it will open the first time during a session that a file with comments is active."
            ))],
            default: 'firstFile',
            description: ( localizeWithPath(_moduleId, 10, "Controls when the comments view should open.")),
            restricted: false
        },
        'comments.useRelativeTime': {
            type: 'boolean',
            default: true,
            description: ( localizeWithPath(
                _moduleId,
                11,
                "Determines if relative time will be used in comment timestamps (ex. '1 day ago')."
            ))
        },
        'comments.visible': {
            type: 'boolean',
            default: true,
            description: ( localizeWithPath(
                _moduleId,
                12,
                "Controls the visibility of the comments bar and comment threads in editors that have commenting ranges and comments. Comments are still accessible via the Comments view and will cause commenting to be toggled on in the same way running the command \"Comments: Toggle Editor Commenting\" toggles comments."
            ))
        },
        'comments.maxHeight': {
            type: 'boolean',
            default: true,
            description: ( localizeWithPath(_moduleId, 13, "Controls whether the comments widget scrolls or expands."))
        },
        'comments.collapseOnResolve': {
            type: 'boolean',
            default: true,
            description: ( localizeWithPath(
                _moduleId,
                14,
                "Controls whether the comment thread should collapse when the thread is resolved."
            ))
        }
    }
});
let UnresolvedCommentsBadge = class UnresolvedCommentsBadge extends Disposable {
    constructor(_commentService, activityService) {
        super();
        this._commentService = _commentService;
        this.activityService = activityService;
        this.activity = this._register(( (new MutableDisposable())));
        this.totalUnresolved = 0;
        this._register(this._commentService.onDidSetAllCommentThreads(this.onAllCommentsChanged, this));
        this._register(this._commentService.onDidUpdateCommentThreads(this.onCommentsUpdated, this));
    }
    onAllCommentsChanged(e) {
        let unresolved = 0;
        for (const thread of e.commentThreads) {
            if (thread.state === CommentThreadState.Unresolved) {
                unresolved++;
            }
        }
        this.updateBadge(unresolved);
    }
    onCommentsUpdated() {
        let unresolved = 0;
        for (const resource of this._commentService.commentsModel.resourceCommentThreads) {
            for (const thread of resource.commentThreads) {
                if (thread.threadState === CommentThreadState.Unresolved) {
                    unresolved++;
                }
            }
        }
        this.updateBadge(unresolved);
    }
    updateBadge(unresolved) {
        if (unresolved === this.totalUnresolved) {
            return;
        }
        this.totalUnresolved = unresolved;
        const message = ( localizeWithPath(_moduleId, 15, '{0} Unresolved Comments', this.totalUnresolved));
        this.activity.value = this.activityService.showViewActivity(COMMENTS_VIEW_ID, { badge: ( (new NumberBadge(this.totalUnresolved, () => message))) });
    }
};
UnresolvedCommentsBadge = ( (__decorate([
    ( (__param(0, ICommentService))),
    ( (__param(1, IActivityService)))
], UnresolvedCommentsBadge)));
( (Registry.as(Extensions$1.Workbench))).registerWorkbenchContribution(UnresolvedCommentsBadge, 4 );

export { UnresolvedCommentsBadge };
